import React, { useState, useRef, MouseEvent, KeyboardEvent, FocusEvent, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  Option,
  Card,
  Snackbar,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  AspectRatio,
  IconButton,
  Alert,
} from '@mui/joy';
import { FaCamera, FaUpload, FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { uploadToOneDrive } from '../utils/graphService';
import { useIsMobile } from '../hooks/useIsMobile';

type ExpenseType = 'Fuel' | 'Lunch' | 'Parking' | 'Supplier Expense' | 'Misc';

// Helper function to format date for filename
const getFormattedDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

const Expenses: React.FC = () => {
  const [expenseType, setExpenseType] = useState<ExpenseType>('Fuel');
  const [isUploading, setIsUploading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadCounterRef = useRef<number>(0);
  const isMobile = useIsMobile();

  // Reset counter at midnight
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();

    const resetCounter = () => {
      uploadCounterRef.current = 0;
    };

    const timeoutId = setTimeout(resetCounter, timeUntilMidnight);
    return () => clearTimeout(timeoutId);
  }, []);

  // Check camera permissions on mobile
  useEffect(() => {
    if (isMobile && 'mediaDevices' in navigator) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(() => setCameraError(null))
        .catch((error) => {
          console.error('Camera permission error:', error);
          setCameraError('Camera access is required for mobile uploads. Please enable camera access in your browser settings.');
        });
    }
  }, [isMobile]);

  const handleExpenseTypeChange = (
    event: MouseEvent<Element> | KeyboardEvent<Element> | FocusEvent<Element> | null,
    value: ExpenseType | null
  ) => {
    if (value) {
      setExpenseType(value);
    }
  };

  const handleCaptureReceipt = () => {
    if (isMobile && cameraError) {
      setNotification({
        message: cameraError,
        type: 'error'
      });
      return;
    }

    if (fileInputRef.current) {
      if (isMobile) {
        fileInputRef.current.accept = 'image/*';
        fileInputRef.current.removeAttribute('capture');
      } else {
        fileInputRef.current.accept = 'image/*,.pdf';
      }
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setNotification({
        message: 'File size must be less than 10MB',
        type: 'error'
      });
      return;
    }

    // For mobile, show preview first
    if (isMobile && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
        setShowPreview(true);
      };
      reader.readAsDataURL(file);
      return;
    }

    // For desktop or non-image files, upload directly
    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    try {
      const now = new Date();
      const month = now.toLocaleString('default', { month: 'long' });
      const year = now.getFullYear();
      const folderPath = `Company/Finance/Receipts/${month} ${year} ${expenseType}`;
      
      const dateStr = getFormattedDate(now);
      const counter = String(++uploadCounterRef.current).padStart(3, '0');
      const fileExtension = file.name.split('.').pop();
      const expenseTypeForFilename = expenseType.replace(/\s+/g, '').toUpperCase();
      const uniqueFilename = `${expenseTypeForFilename}_${dateStr}_${counter}.${fileExtension}`;

      const uploadResult = await uploadToOneDrive(file, folderPath, uniqueFilename);
      
      if (uploadResult.success) {
        setNotification({
          message: 'Receipt uploaded successfully!',
          type: 'success'
        });
      } else {
        throw new Error(uploadResult.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({
        message: error instanceof Error ? error.message : 'Failed to upload receipt',
        type: 'error'
      });
    } finally {
      setIsUploading(false);
      setPreviewImage(null);
      setShowPreview(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePreviewConfirm = async () => {
    if (previewImage && fileInputRef.current?.files?.[0]) {
      await uploadFile(fileInputRef.current.files[0]);
    }
  };

  const handlePreviewCancel = () => {
    setPreviewImage(null);
    setShowPreview(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ 
      px: { xs: 1, sm: 4 }, 
      py: { xs: 2, sm: 4 }, 
      maxWidth: { xs: '100vw', md: 800 }, 
      mx: 'auto',
    }}>
      <Typography level="h2" mb={2}>Expenses</Typography>
      
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography level="h4" mb={2}>Upload Receipt</Typography>
        
        <Box sx={{ width: 320, maxWidth: '100%', mx: 'auto' }}>
          <Typography level="body-sm" mb={1}>Expense Type</Typography>
          <Select
            value={expenseType}
            onChange={handleExpenseTypeChange}
            sx={{ width: '100%' }}
          >
            <Option value="Fuel">Fuel</Option>
            <Option value="Lunch">Lunch</Option>
            <Option value="Parking">Parking</Option>
            <Option value="Supplier Expense">Supplier Expense</Option>
            <Option value="Misc">Misc</Option>
          </Select>

          {cameraError && (
            <Alert 
              color="danger" 
              startDecorator={<span>{FaExclamationTriangle({})}</span>}
              sx={{ mb: 2 }}
            >
              {cameraError}
            </Alert>
          )}

          <Button
            startDecorator={
              isUploading ? (
                <CircularProgress size="sm" />
              ) : (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  {isMobile ? FaCamera({}) : FaUpload({})}
                </Box>
              )
            }
            onClick={handleCaptureReceipt}
            disabled={isUploading || (isMobile && !!cameraError)}
            sx={{ width: '100%', mt: 2 }}
          >
            {isUploading ? 'Uploading...' : (isMobile ? 'Capture Receipt' : 'Select Receipt')}
          </Button>
        </Box>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          accept={isMobile ? 'image/*' : 'image/*,.pdf'}
        />
      </Card>

      {/* Image Preview Modal */}
      <Modal 
        open={showPreview} 
        onClose={handlePreviewCancel}
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: { xs: '90vw', sm: 500 },
            width: '100%',
            p: 2,
          }}
        >
          <ModalClose />
          <Typography level="h4" mb={2}>Preview Receipt</Typography>
          
          <AspectRatio ratio="3/4" sx={{ mb: 2 }}>
            {previewImage && (
              <img
                src={previewImage}
                alt="Receipt preview"
                style={{ objectFit: 'contain' }}
              />
            )}
          </AspectRatio>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="neutral"
              onClick={handlePreviewCancel}
              startDecorator={<span>{FaTimes({})}</span>}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePreviewConfirm}
              startDecorator={<span>{FaCheck({})}</span>}
              loading={isUploading}
            >
              Upload
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        variant="soft"
        color={notification?.type === 'success' ? 'success' : 'danger'}
        startDecorator={
          notification?.type === 'success' ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              {FaCheck({})}
            </Box>
          ) : (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              {FaExclamationTriangle({})}
            </Box>
          )
        }
        onClose={() => setNotification(null)}
        autoHideDuration={5000}
        sx={{
          '& .MuiSnackbarContent-message': {
            flex: 1,
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center'
          }
        }}
      >
        {notification?.message}
      </Snackbar>
    </Box>
  );
};

export default Expenses; 